// Layout Section Variants Configuration
// Mobile App, Footer, Subscribe, Program Cards
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import {
  setMobileApp1,
  setFooter1,
  setFooter2,
  setFooter3,
  setSubscribe1,
  setProgramCards1,
  setProgramCards2,
  setProgramCards3,
  setProgramCards4,
  setProgramCards5,
  setProgramCards1027,
  setProgramCards1053,
  setProgramCards1038,
  setProgramCards1045,
  setProgramCards1067,
  setProgramCards1070,
  setProgramCards6002,
  setFooter1034,
  setFooter5014,
} from "@/store/slices/page";
import { Form_1, Form_3 } from "@/components/organisms/admin/forms";
import type { PageState } from "@/store/slices/page";
import { MOBILE_APP_1_CONSTANTS } from "../fields/mobileApp/mobileApp_1";
import { FOOTER_1_CONSTANTS } from "../fields/footer/footer_1";
import { FOOTER_2_CONSTANTS } from "../fields/footer/footer_2";
import { FOOTER_3_CONSTANTS } from "../fields/footer/footer_3";
import { SUBSCRIBE_1_CONSTANTS } from "../fields/subscribe/subscribe_1";
import { PROGRAM_CARDS_1_CONSTANTS } from "../fields/programCards/programCards_1";
import { PROGRAM_CARDS_2_CONSTANTS } from "../fields/programCards/programCards_2";
import { PROGRAM_CARDS_3_CONSTANTS } from "../fields/programCards/programCards_3";
import { PROGRAM_CARDS_4_CONSTANTS } from "../fields/programCards/programCards_4";
import { createMobileApp, updateMobileApp } from "@/api/admin/mobileApp";
import {
  createFooterCategory,
  updateFooterCategory,
  deleteFooterCategory,
  getFooterCategoriesByComponentId,
  createFooterItem,
  updateFooterItem,
  deleteFooterItem,
  getFooterItemsByCategoryId,
  getFooterByComponentId,
  cloneFooterWithCategories,
} from "@/api/admin/footer";
import { createSubscribe, updateSubscribe } from "@/api/admin/subscribe";
import {
  createProgramCards,
  updateProgramCards,
  getProgramCardsByComponentId,
  getProgramCardsCategoriesByComponentId,
  createProgramCardsCategory,
  updateProgramCardsCategory,
  deleteProgramCardsCategory,
  getProgramCardsCardsByCategoryId,
  createProgramCardsCard,
  updateProgramCardsCard,
  deleteProgramCardsCard,
  cloneProgramCardsWithCategories,
} from "@/api/admin/programCards";
import { getActiveCourses } from "@/api/admin/course";
import MobileApp1 from "@/widgets/mobileapp/MobileApp1";
import Footer1 from "@/widgets/footer/Footer1";
import Footer2 from "@/widgets/footer/Footer2";
import Footer3 from "@/widgets/footer/Footer3";
import Subscribe1 from "@/widgets/subscribe/Subscribe1";
import ExploreCourses1 from "@/widgets/exploreonlinecourses/ExploreCourses1";
import { ProgramCards2 } from "@/widgets/exploreonlinecourses/ProgramCards2";
import ProgramCards3 from "@/widgets/exploreonlinecourses/ProgramCards3";
import ProgramCards4 from "@/widgets/exploreonlinecourses/ProgramCards4";
import { BlogProgramCards1 } from "@/widgets/exploreonlinecourses/BlogProgramCards1";
import {
  SECTION_TYPES,
  MOBILE_APP_VARIANTS,
  FOOTER_VARIANTS,
  SUBSCRIBE_VARIANTS,
  PROGRAM_CARDS_VARIANTS,
} from "../sectionTypes";
import {
  extractMetadataFields,
  createFooterComponent,
  updateFooterComponent,
  createProgramCardsComponent,
  updateProgramCardsComponent,
} from "@/helpers";
import { getPreviewData, activateMetadata } from "@/utils/variantHelpers";
import { PROGRAM_CARDS_1027_CONSTANTS } from "../fields/programCards/programCards_1027";
import { PROGRAM_CARDS_1053_CONSTANTS } from "../fields/programCards/programCards_1053";
import ProgramCards1027 from "@/widgets/exploreonlinecourses/ProgramCards1027";
import ProgramCards1053 from "@/widgets/exploreonlinecourses/ProgramCards1053";
import { PROGRAM_CARDS_1038_CONSTANTS } from "../fields/programCards/programCards_1038";
import ProgramCards1038 from "@/widgets/exploreonlinecourses/ProgramCards1038";
import { PROGRAM_CARDS_1045_CONSTANTS } from "../fields/programCards/programCards_1045";
import { PROGRAM_CARDS_1067_CONSTANTS } from "../fields/programCards/programCards_1067";
import { PROGRAM_CARDS_1070_CONSTANTS } from "../fields/programCards/programCards_1070";
import { PROGRAM_CARDS_6002_CONSTANTS } from "../fields/programCards/programCards_6002";
import ProgramCards1045 from "@/widgets/exploreonlinecourses/ProgramCards1045";
import ProgramCards1067 from "@/widgets/exploreonlinecourses/ProgramCards1067";
import ProgramCards1070 from "@/widgets/exploreonlinecourses/ProgramCards1070";
import ProgramCards6002 from "@/widgets/exploreonlinecourses/ProgramCards6002";
import { FOOTER_5014_CONSTANTS } from "../fields/footer/footer_5014";
import { FOOTER_1034_CONSTANTS } from "../fields/footer/footer_1034";
import Footer5014 from "@/widgets/footer/Footer5014";
import Footer1034 from "@/widgets/footer/Footer1034";

export const LAYOUT_SECTION_VARIANTS = {
  [SECTION_TYPES.MOBILE_APP]: [
    {
      id: MOBILE_APP_VARIANTS.MOBILE_APP_1,
      name: "Mobile App 1",
      description: "Mobile app information",
      componentFields: MOBILE_APP_1_CONSTANTS.MOBILE_APP_FIELDS_CONFIG,
      ...extractMetadataFields(MOBILE_APP_1_CONSTANTS),
      showTable: false,
      setAction: setMobileApp1,
      formComponent: Form_1,
      previewComponent: MobileApp1,
      selector: (state: PageState) => state.mobile_app_1,
      createMethod: createMobileApp,
      updateMethod: updateMobileApp,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.FOOTER]: [
    {
      id: FOOTER_VARIANTS.FOOTER_1,
      name: "Footer 1",
      description: "Page footer with categories and links",
      componentFields: FOOTER_1_CONSTANTS.FOOTER_FIELDS_CONFIG,
      categoryFieldsConfig: FOOTER_1_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FOOTER_1_CONSTANTS.FOOTER_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFooter1,
      formComponent: Form_3,
      previewComponent: Footer1,
      selector: (state: PageState) => state.footer_1,
      createMethod: createFooterComponent,
      updateMethod: updateFooterComponent,
      getMethod: getFooterByComponentId,
      fetchCategoriesMethod: getFooterCategoriesByComponentId,
      createCategoryMethod: createFooterCategory,
      updateCategoryMethod: updateFooterCategory,
      deleteCategoryMethod: deleteFooterCategory,
      fetchItemsMethod: getFooterItemsByCategoryId,
      createItemMethod: createFooterItem,
      updateItemMethod: updateFooterItem,
      deleteItemMethod: deleteFooterItem,
      cloneMethod: cloneFooterWithCategories,
      cloneEntityName: "Footer",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: ({ section }: any) => {
        const data = section.data || {};
        return {
          data: {
            data: {
              ...data,
              categories: data.categories || [],
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
              status: true,
            },
          },
        };
      },
    },
    {
      id: FOOTER_VARIANTS.FOOTER_2,
      name: "Footer 2",
      description: "Minimal footer bar with gradient and copyright",
      componentFields: FOOTER_2_CONSTANTS.FOOTER_FIELDS_CONFIG,
      showTable: false,
      setAction: setFooter2,
      formComponent: Form_1,
      previewComponent: Footer2,
      selector: (state: PageState) => state.footer_2,
      createMethod: createFooterComponent,
      updateMethod: updateFooterComponent,
      getMethod: getFooterByComponentId,
      preparePreviewData: ({ section }: any) => {
        const data = section.data || {};
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: FOOTER_VARIANTS.FOOTER_3,
      name: "Footer 3",
      description: "Multi-column footer — set column count; assign each category to a column, then add link items",
      componentFields: FOOTER_3_CONSTANTS.FOOTER_FIELDS_CONFIG,
      categoryFieldsConfig: FOOTER_3_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FOOTER_3_CONSTANTS.FOOTER_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFooter3,
      formComponent: Form_3,
      previewComponent: Footer3,
      selector: (state: PageState) => state.footer_3,
      createMethod: createFooterComponent,
      updateMethod: updateFooterComponent,
      getMethod: getFooterByComponentId,
      fetchCategoriesMethod: getFooterCategoriesByComponentId,
      createCategoryMethod: createFooterCategory,
      updateCategoryMethod: updateFooterCategory,
      deleteCategoryMethod: deleteFooterCategory,
      fetchItemsMethod: getFooterItemsByCategoryId,
      createItemMethod: createFooterItem,
      updateItemMethod: updateFooterItem,
      deleteItemMethod: deleteFooterItem,
      cloneMethod: cloneFooterWithCategories,
      cloneEntityName: "Footer",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: ({ section }: any) => {
        const data = section.data || {};
        return {
          data: {
            data: {
              ...data,
              categories: data.categories || [],
              column_count: data.column_count,
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
              status: true,
            },
          },
        };
      },
    },
    {
      id: FOOTER_VARIANTS.FOOTER_1034,
      name: "Footer 1034 — BDU Footer",
      description:
        "BDU Footer — background art, link columns, disclaimer and copyright",
      componentFields: FOOTER_1034_CONSTANTS.FOOTER_FIELDS_CONFIG,
      categoryFieldsConfig: FOOTER_1034_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FOOTER_1034_CONSTANTS.FOOTER_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFooter1034,
      formComponent: Form_3,
      previewComponent: Footer1034,
      selector: (state: PageState) => state.footer_1034,
      createMethod: createFooterComponent,
      updateMethod: updateFooterComponent,
      getMethod: getFooterByComponentId,
      fetchCategoriesMethod: getFooterCategoriesByComponentId,
      createCategoryMethod: createFooterCategory,
      updateCategoryMethod: updateFooterCategory,
      deleteCategoryMethod: deleteFooterCategory,
      fetchItemsMethod: getFooterItemsByCategoryId,
      createItemMethod: createFooterItem,
      updateItemMethod: updateFooterItem,
      deleteItemMethod: deleteFooterItem,
      cloneMethod: cloneFooterWithCategories,
      cloneEntityName: "Footer",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const merged = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...merged,
              categories: Array.isArray(merged?.categories)
                ? merged.categories
                : [],
              status: merged?.status ?? true,
            },
          },
        };
      },
    },
{
      id: FOOTER_VARIANTS.FOOTER_5014,
      name: "Footer 5014 — KUK Footer",
      description:
        "KUK footer — a solid band with optional link columns and a centred copyright bar, each part toggled independently",
      componentFields: FOOTER_5014_CONSTANTS.FOOTER_FIELDS_CONFIG,
      categoryFieldsConfig: FOOTER_5014_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FOOTER_5014_CONSTANTS.FOOTER_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFooter5014,
      formComponent: Form_3,
      previewComponent: Footer5014,
      selector: (state: PageState) => state.footer_5014,
      createMethod: createFooterComponent,
      updateMethod: updateFooterComponent,
      getMethod: getFooterByComponentId,
      fetchCategoriesMethod: getFooterCategoriesByComponentId,
      createCategoryMethod: createFooterCategory,
      updateCategoryMethod: updateFooterCategory,
      deleteCategoryMethod: deleteFooterCategory,
      fetchItemsMethod: getFooterItemsByCategoryId,
      createItemMethod: createFooterItem,
      updateItemMethod: updateFooterItem,
      deleteItemMethod: deleteFooterItem,
      cloneMethod: cloneFooterWithCategories,
      cloneEntityName: "Footer",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const merged = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...merged,
              categories: Array.isArray(merged?.categories)
                ? merged.categories
                : [],
              status: merged?.status ?? true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.SUBSCRIBE]: [
    {
      id: SUBSCRIBE_VARIANTS.SUBSCRIBE_1,
      name: "Subscribe 1",
      description: "Newsletter subscription section",
      componentFields: SUBSCRIBE_1_CONSTANTS.SUBSCRIBE_FIELDS_CONFIG,
      ...extractMetadataFields(SUBSCRIBE_1_CONSTANTS),
      showTable: false,
      setAction: setSubscribe1,
      formComponent: Form_1,
      previewComponent: Subscribe1,
      selector: (state: PageState) => state.subscribe_1,
      createMethod: createSubscribe,
      updateMethod: updateSubscribe,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.PROGRAM_CARDS]: [
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1,
      name: "Program Cards 1",
      description: "Program cards with course information",
      componentFields: PROGRAM_CARDS_1_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1,
      formComponent: Form_1,
      previewComponent: ExploreCourses1,
      selector: (state: PageState) => state.program_cards_1,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      dynamicOptionsConfig: {
        courses: {
          fetch: getActiveCourses,
          filter: (course: any) => course.brochure?.trim(),
          transform: (course: any) => ({
            value: String(course.id),
            label: course.program_name,
            brochure: course.brochure,
          }),
        },
      },
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              title: data.title || "Our Programs",
              title_text_color: data.title_text_color || "#000000",
              card_background_color: data.card_background_color || "#ffffff",
              load_more_text: data.load_more_text || "Load More",
              load_more_desktop_icon: data.load_more_desktop_icon || "",
              load_more_desktop_icon_alt: data.load_more_desktop_icon_alt || "",
              load_more_mobile_icon: data.load_more_mobile_icon || "",
              load_more_mobile_icon_alt: data.load_more_mobile_icon_alt || "",
              load_more_desktop_text_color:
                data.load_more_desktop_text_color || "#000000",
              load_more_mobile_text_color:
                data.load_more_mobile_text_color || "#000000",
              load_more_desktop_background_color:
                data.load_more_desktop_background_color || "#ffffff",
              load_more_mobile_background_color:
                data.load_more_mobile_background_color || "#ffffff",
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_2,
      name: "Program Cards 2",
      description: "Programs Built for Every Stage – categories, cards, modal with Apply Now CTA",
      componentFields: PROGRAM_CARDS_2_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      categoryFieldsConfig: PROGRAM_CARDS_2_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: PROGRAM_CARDS_2_CONSTANTS.CARD_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setProgramCards2,
      formComponent: Form_3,
      previewComponent: ProgramCards2,
      selector: (state: PageState) => state.program_cards_2,
      createMethod: createProgramCardsComponent,
      updateMethod: updateProgramCardsComponent,
      getMethod: getProgramCardsByComponentId,
      fetchCategoriesMethod: getProgramCardsCategoriesByComponentId,
      createCategoryMethod: createProgramCardsCategory,
      updateCategoryMethod: updateProgramCardsCategory,
      deleteCategoryMethod: deleteProgramCardsCategory,
      fetchItemsMethod: getProgramCardsCardsByCategoryId,
      createItemMethod: createProgramCardsCard,
      updateItemMethod: updateProgramCardsCard,
      deleteItemMethod: deleteProgramCardsCard,
      cloneMethod: cloneProgramCardsWithCategories,
      cloneEntityName: "Program Cards",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      supportsOptionalCategoriesToggle: true,
      optionalCategoriesDirectParentKey: "program_cards_id",
      optionalCategoriesParentIdErrorMessage:
        "Save the Program Cards component settings before adding cards.",
      optionalCategoriesFlatUi: {
        listHeading: "Program cards",
        addButtonLabel: "Add card",
        emptyMessage: 'No cards yet. Click "Add card" to create one.',
        needsSaveHint:
          "Click Update Component above to turn off categories on the server, then the card list will refresh.",
        itemEditTitle: "Edit card",
        itemAddTitle: "Add card",
        deleteTitle: "Delete card",
        deleteDescription:
          "Are you sure you want to delete this card? This action cannot be undone.",
      },
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const categories = Array.isArray(data.categories) ? data.categories : [];
        const useCategoriesOn = !(data.use_categories === false || data.use_categories === 0);
        return {
          data: {
            data: {
              ...data,
              categories,
              status: true,
              font_family_variant: data.font_family_variant || "variant_2",
              cta_button_variant: data.cta_button_variant || "variant_2",
              show_features: Boolean(data.show_features),
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
              use_categories: useCategoriesOn,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_3,
      name: "Program Cards 3",
      description:
        "Explore Our Online Programs – category tabs, badged program cards (Trending/New), featured card & View more button",
      componentFields: PROGRAM_CARDS_3_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      categoryFieldsConfig: PROGRAM_CARDS_3_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: PROGRAM_CARDS_3_CONSTANTS.CARD_TABLE_FIELDS_CONFIG,
      // Card table starts with image / image_alt — label the list rows with the card copy instead.
      itemTitleFieldName: "title",
      itemDescFieldName: "subtitle",
      showTable: false,
      setAction: setProgramCards3,
      formComponent: Form_3,
      previewComponent: ProgramCards3,
      selector: (state: PageState) => state.program_cards_3,
      createMethod: createProgramCardsComponent,
      updateMethod: updateProgramCardsComponent,
      getMethod: getProgramCardsByComponentId,
      fetchCategoriesMethod: getProgramCardsCategoriesByComponentId,
      createCategoryMethod: createProgramCardsCategory,
      updateCategoryMethod: updateProgramCardsCategory,
      deleteCategoryMethod: deleteProgramCardsCategory,
      fetchItemsMethod: getProgramCardsCardsByCategoryId,
      createItemMethod: createProgramCardsCard,
      updateItemMethod: updateProgramCardsCard,
      deleteItemMethod: deleteProgramCardsCard,
      cloneMethod: cloneProgramCardsWithCategories,
      cloneEntityName: "Program Cards",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      supportsOptionalCategoriesToggle: true,
      optionalCategoriesDirectParentKey: "program_cards_id",
      optionalCategoriesParentIdErrorMessage:
        "Save the Program Cards component settings before adding cards.",
      optionalCategoriesFlatUi: {
        listHeading: "Program cards",
        addButtonLabel: "Add card",
        emptyMessage: 'No cards yet. Click "Add card" to create one.',
        needsSaveHint:
          "Click Update Component above to turn off categories on the server, then the card list will refresh.",
        itemEditTitle: "Edit card",
        itemAddTitle: "Add card",
        deleteTitle: "Delete card",
        deleteDescription:
          "Are you sure you want to delete this card? This action cannot be undone.",
      },
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const categories = Array.isArray(data.categories) ? data.categories : [];
        const useCategoriesOn = !(data.use_categories === false || data.use_categories === 0);
        return {
          data: {
            data: {
              ...data,
              categories,
              status: true,
              use_categories: useCategoriesOn,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_4,
      name: "Program Cards 4",
      description:
        "Careers after course – carousel of career cards (image, title, salary, key skills) with background image",
      componentFields: PROGRAM_CARDS_4_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_4_CONSTANTS),
      showTable: true,
      setAction: setProgramCards4,
      formComponent: Form_1,
      previewComponent: ProgramCards4,
      selector: (state: PageState) => state.program_cards_4,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              title: data.title || "Careers After {This Course}",
              title_text_color: data.title_text_color || "#000000",
              title_highlight_color: data.title_highlight_color || "#E1473F",
              salary_text_color: data.salary_text_color || "#E1473F",
              key_skills_text_color: data.key_skills_text_color || "#171719",
              key_skills_background_color:
                data.key_skills_background_color || "#F3F4F6",
              key_skills_border_color:
                data.key_skills_border_color || "#FF9944",
              description: data.description || "",
              note: data.note || "",
              background_image: data.background_image || "",
              background_image_alt: data.background_image_alt || "",
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1038,
      name: "Program Cards 1038 — BDU Specialisations",
      description:
        "BDU Specialisations — centred heading with a gradient rule and a wrapping grid of tinted cards",
      componentFields: PROGRAM_CARDS_1038_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1038_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1038,
      formComponent: Form_1,
      previewComponent: ProgramCards1038,
      selector: (state: PageState) => state.program_cards_1038,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1045,
      name: "Program Cards 1045 — BDU Other Specialisations",
      description:
        "BDU Other Specialisations — left-aligned eyebrow + heading over a horizontal card rail",
      componentFields: PROGRAM_CARDS_1045_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1045_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1045,
      formComponent: Form_1,
      previewComponent: ProgramCards1045,
      selector: (state: PageState) => state.program_cards_1045,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1067,
      name: "Program Cards 1067 — KUK Explore other Specialisations",
      description:
        "KUK Explore other Specialisations — an eyebrow heading in a left column beside text cards with Know More and Brochure, collapsing to tinted thumbnail rows below 768px",
      componentFields: PROGRAM_CARDS_1067_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1067_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1067,
      formComponent: Form_1,
      previewComponent: ProgramCards1067,
      selector: (state: PageState) => state.program_cards_1067,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1070,
      name: "Program Cards 1070 — KUK Explore other courses",
      description:
        "KUK Explore other courses — a full-bleed artwork band over a four-up image-card grid, becoming a horizontal rail below 768px",
      componentFields: PROGRAM_CARDS_1070_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1070_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1070,
      formComponent: Form_1,
      previewComponent: ProgramCards1070,
      selector: (state: PageState) => state.program_cards_1070,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_6002,
      name: "Program Cards 6002 — BDU Auxiliary Explore Our Programs",
      description:
        "BDU auxiliary pages — tinted band with a heading, gradient rule and a horizontal programme card rail",
      componentFields: PROGRAM_CARDS_6002_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_6002_CONSTANTS),
      showTable: true,
      setAction: setProgramCards6002,
      formComponent: Form_1,
      previewComponent: ProgramCards6002,
      selector: (state: PageState) => state.program_cards_6002,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1053,
      name: "Program Cards 1053 — KUK Home Featured Programmes",
      description:
        "KUK home Featured Programmes — level tabs, a selectable programme list, a detail card with Know-more and Brochure CTAs, and the selected programme's specialisation grid",
      componentFields: PROGRAM_CARDS_1053_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1053_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1053,
      formComponent: Form_1,
      previewComponent: ProgramCards1053,
      selector: (state: PageState) => state.program_cards_1053,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_1027,
      name: "Program Cards 1027 — BDU Featured Programmes",
      description: "BDU Featured Programmes — tabbed groups of tinted image cards",
      componentFields: PROGRAM_CARDS_1027_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1027_CONSTANTS),
      showTable: true,
      setAction: setProgramCards1027,
      formComponent: Form_1,
      previewComponent: ProgramCards1027,
      selector: (state: PageState) => state.program_cards_1027,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_BLOG_1,
      name: "Blog program cards",
      description:
        "Course cards in the blog article column (title + carousel); same fields as Program cards 1",
      componentFields: PROGRAM_CARDS_1_CONSTANTS.PROGRAM_CARDS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_CARDS_1_CONSTANTS),
      showTable: true,
      setAction: setProgramCards5,
      formComponent: Form_1,
      previewComponent: BlogProgramCards1,
      selector: (state: PageState) => state.program_cards_5,
      createMethod: createProgramCards,
      updateMethod: updateProgramCards,
      dynamicOptionsConfig: {
        courses: {
          fetch: getActiveCourses,
          filter: (course: any) => course.brochure?.trim(),
          transform: (course: any) => ({
            value: String(course.id),
            label: course.program_name,
            brochure: course.brochure,
          }),
        },
      },
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              title: data.title || "Our Programs",
              title_text_color: data.title_text_color || "#000000",
              card_background_color: data.card_background_color || "#ffffff",
              load_more_text: data.load_more_text || "Load More",
              load_more_desktop_icon: data.load_more_desktop_icon || "",
              load_more_desktop_icon_alt: data.load_more_desktop_icon_alt || "",
              load_more_mobile_icon: data.load_more_mobile_icon || "",
              load_more_mobile_icon_alt: data.load_more_mobile_icon_alt || "",
              load_more_desktop_text_color:
                data.load_more_desktop_text_color || "#000000",
              load_more_mobile_text_color:
                data.load_more_mobile_text_color || "#000000",
              load_more_desktop_background_color:
                data.load_more_desktop_background_color || "#ffffff",
              load_more_mobile_background_color:
                data.load_more_mobile_background_color || "#ffffff",
              metadata: activateMetadata(data.metadata),
              status: true,
            },
          },
        };
      },
    },
  ],
};
